// Russian grammar rules the editor checks on the client, while you type
// (docs/research/editor-error-underline.md §9.3).
//
// Ported from LanguageTool's Russian rules —
// languagetool-language-modules/ru/src/main/resources/org/languagetool/rules/ru/grammar.xml,
// https://github.com/languagetool-org/languagetool
// Copyright © 2008-2023 Yakov Reztsov and the LanguageTool contributors.
// LanguageTool is free software under the GNU Lesser General Public License,
// version 2.1 or later (https://www.gnu.org/licenses/old-licenses/lgpl-2.1.html);
// these rules, as adapted here, are offered under the same licence. Each rule
// names the LanguageTool rule (group) it was ported from in `lt`; the notice
// that ships with the app is `public/third-party/languagetool.txt`.
//
// Only the lexical rules are ported: those whose tokens are fixed words. A
// LanguageTool `inflected` token (any form of a lemma) is spelled out here as
// the forms a script uses; a rule that needs a part-of-speech tag has no port.
// Kept out on purpose, after the research's run against LanguageTool:
// `Comma_before_kak` (its message about «Привет , как» misleads), the other
// comma rules (patchy without a parser), and the rules whose correct reading is
// as common in a script as the error («закончить школу», «в виду»).

export interface GrammarToken {
    /** `[from, to)` in the text, UTF-16 code units. */
    from: number;
    to: number;
    text: string;
}

/** One token of a pattern: a `|`-alternation of whole words (case-insensitive
 *  unless the rule says otherwise), or a test of its own. */
export type TokenTest = string | ((token: GrammarToken) => boolean);

export interface GrammarRule {
    id: string;
    /** The LanguageTool rule (group) id this one was ported from. Some LT ids
     *  mix in Cyrillic look-alike letters (`V_TECHENIЕ`): copy, don't retype. */
    lt: string;
    /** Alternative token sequences; the first that matches at a token wins. */
    patterns: readonly (readonly TokenTest[])[];
    /** The tokens underlined, `[first, last]`, inclusive; the rest is context.
     *  Omitted: the whole pattern. */
    marked?: readonly [first: number, last: number];
    /** Its word tests read case as written («а» is not «А»). */
    caseSensitive?: true;
    /** The token after the match makes it no error. A rule that has one
     *  waits for that token: a match with nothing after it is undecided. */
    unless?: (next: GrammarToken) => boolean;
    /** The fix, from the marked tokens as the line has them. */
    fix: (words: readonly string[]) => string;
}

// ── Case ────────────────────────────────────────────────────────────────

const isAllCaps = (text: string) => /\p{L}.*\p{L}/u.test(text) && text === text.toUpperCase() && text !== text.toLowerCase();

/** `word` in the case of `model`: ALL CAPS, Capitalised, or as it is. */
export function caseLike(model: string, word: string): string {
    if (isAllCaps(model)) return word.toUpperCase();
    const first = model.charAt(0);
    return first !== first.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

// ── Token tests and fixes ───────────────────────────────────────────────

const isWord = (t: GrammarToken) => /^\p{L}/u.test(t.text);
const isRussianWord = (t: GrammarToken) => /^\p{Script=Cyrillic}[\p{Script=Cyrillic}-]*$/u.test(t.text);
/** A word or a number — not a punctuation mark, not masked markup. */
const isWordOrNumber = (t: GrammarToken) => /^[\p{L}\p{N}]/u.test(t.text);
const PREPOSITION_RE = /^(?:в|во|на|с|со|под|за|из|от|к|ко|по|у|для|без|над|о|об|при|через|до)$/iu;
const notPreposition = (t: GrammarToken) => isWord(t) && !PREPOSITION_RE.test(t.text);

/** The marked words with the `i`-th replaced by `word`, in its case. */
const swap = (i: number, word: string) => (words: readonly string[]) => words.map((w, j) => (j === i ? caseLike(w, word) : w)).join(' ');
/** The marked words replaced by `phrase`, in the case of the first. */
const phrase = (fixed: string) => (words: readonly string[]) => caseLike(words.join(' '), fixed);
/** The first two marked words written as one, the rest after them. */
const glue = (words: readonly string[]) => [`${words[0] ?? ''}${words[1] ?? ''}`, ...words.slice(2)].join(' ');
/** The first word's start rewritten (`одел` → `надел`), in its case. */
const restem = (from: RegExp, to: string) => (words: readonly string[]) =>
    words.map((w, j) => (j === 0 ? caseLike(w, w.toLowerCase().replace(from, to)) : w)).join(' ');
const firstWord = (words: readonly string[]) => words[0] ?? '';
/** A comma after the first marked word. */
const comma = (words: readonly string[]) => `${words[0] ?? ''}, ${words[1] ?? ''}`;

// ── Word lists ──────────────────────────────────────────────────────────

/** «одеть» in the forms a script uses. */
const ODET =
    'одел|одела|одели|одело|одеть|одену|оденешь|оденет|оденем|оденете|оденут|одень|оденьте|одевать|одеваю|одеваешь|одевает|одеваем|одеваете|одевают|одевал|одевала|одевали|одевай|одевайте|одев|одевая|одето|одет|одета|одеты';
const CLOTHES =
    'рубашку|рубашки|одежду|платье|платья|юбку|юбки|пальто|куртку|куртки|брюки|майку|майки|носки|носок|обувь|туфли|сапоги|ботинки|сарафан|' +
    'пиджак|свитер|халат|джинсы|кофту|футболку|плащ|шапку|шляпу|шарф|перчатки|галстук';
const FOOTWEAR = 'обувь|сапоги|сапог|босоножки|туфли|ботинки|ботинок|кроссовки|кеды|тапки|тапочки';
const TIME =
    'часа|получаса|минуты|секунды|промежутка|дня|суток|времени|недели|месяца|года|столетия|тысячелетия|вечера|ночи|утра|сезона|жизни';
const TIMES = `${TIME}|часов|минут|секунд|дней|недель|месяцев|лет|столетий|календарных|рабочих|банковских|выходных`;
const AMOUNT =
    'двух|трёх|трех|четырёх|четырех|пяти|шести|семи|восьми|девяти|десяти|пятнадцати|двадцати|тридцати|сорока|нескольких|многих|' +
    'долгих|долгого|долгой|всего|всей|целого|целой|этого|этой|этих|того|той|последних|ближайших|первых|следующих';
const AMOUNT_RE = new RegExp(`^(?:\\p{N}+|${AMOUNT})$`, 'iu');
const isAmount = (t: GrammarToken) => AMOUNT_RE.test(t.text);
const POSSESSIVES = 'моей|твоей|вашей|нашей|своей|своего|их|его|ее|её|моих|твоих|ваших|наших';
/** Who «не смотря на» looks at — then it is «не глядя на», not «несмотря
 *  на». LanguageTool asks the tagger for any animate noun; with no tagger,
 *  the pronouns and the people a script most often looks at, and a name
 *  (Capitalised, not a shout in capitals). */
const LOOKED_AT_RE =
    /^(?:него|неё|нее|них|меня|тебя|нас|вас|себя|друг|людей|прохожих|девушку|девочку|парня|мальчика|мужчину|женщину|ребёнка|ребенка|детей|отца|мать|маму|папу|брата|сестру|сына|дочь|друга|подругу|собеседника|гостя|гостей|человека|собаку|кота|кошку)$/iu;
const isName = (t: GrammarToken) => /^\p{Lu}\p{Ll}/u.test(t.text);

// ── The rules ───────────────────────────────────────────────────────────

export const RU_GRAMMAR_RULES = [
    {
        id: 'ne-smotrya-na',
        lt: 'Pravopisanie_slitno1',
        patterns: [['не', 'смотря|взирая', 'на']],
        unless: next => LOOKED_AT_RE.test(next.text) || isName(next),
        fix: glue,
    },
    {
        id: 'v-techenie',
        lt: 'V_TECHENIЕ',
        patterns: [
            ['в', 'течении', TIME],
            ['в', 'течении', isAmount, TIMES],
        ],
        marked: [0, 1],
        fix: swap(1, 'течение'),
    },
    { id: 'v-techenii', lt: 'V_TECHENII', patterns: [['в', 'течение', 'реки|потока|воды|ручья']], marked: [0, 1], fix: swap(1, 'течении') },
    { id: 'v-otlichie', lt: 'V_OTLICHIE', patterns: [['в', 'отличии', 'от']], marked: [0, 1], fix: swap(1, 'отличие') },
    {
        id: 'v-prodolzhenie',
        lt: 'V_PRODOLJENIЕ',
        patterns: [['в', 'продолжении', `${TIME}|разговора|беседы`]],
        marked: [0, 1],
        fix: swap(1, 'продолжение'),
    },
    {
        id: 'v-zaklyuchenie',
        lt: 'Predlog_v_zakluchenije',
        patterns: [
            [
                'в',
                'заключении',
                `этого|чего|всего|которых|этой|того|той|${POSSESSIVES}|выступления|мероприятия|собрания|встречи|концерта|эфира|вышесказанного|позвольте|вышеупомянутого`,
            ],
        ],
        marked: [0, 1],
        fix: swap(1, 'заключение'),
    },
    {
        id: 'vsledstvie',
        lt: 'Predlogi_vsledstvije_napodobije',
        patterns: [['в', 'следствии|следствие', `этого|чего|этих|этой|того|той|которого|которой|которых|${POSSESSIVES}`]],
        marked: [0, 1],
        fix: phrase('вследствие'),
    },
    {
        id: 'napodobie',
        lt: 'Predlogi_vsledstvije_napodobije',
        patterns: [['на', 'подобии', `тех|этих|этой|того|той|этого|${POSSESSIVES}`]],
        marked: [0, 1],
        fix: phrase('наподобие'),
    },
    {
        id: 'odet-nadet',
        lt: 'Logical_odel_nadel',
        patterns: [
            [ODET, CLOTHES],
            [ODET, notPreposition, CLOTHES],
        ],
        marked: [0, 0],
        fix: restem(/^од/, 'над'),
    },
    {
        id: 'pereodet-pomenyat',
        lt: 'Logical_odel_nadel',
        patterns: [['переодел|переодела|переодели|переодело|переодеть', CLOTHES]],
        marked: [0, 0],
        fix: restem(/^переоде/, 'поменя'),
    },
    {
        id: 'obut-nadet',
        lt: 'Logical_odel_nadel',
        patterns: [['обул|обула|обули|обуть', FOOTWEAR]],
        marked: [0, 0],
        fix: restem(/^обу/, 'наде'),
    },
    {
        id: 'razut-snyat',
        lt: 'Logical_odel_nadel',
        patterns: [['разул|разула|разули|разуть', FOOTWEAR]],
        marked: [0, 0],
        fix: restem(/^разу/, 'сня'),
    },
    {
        id: 'comma-before-a',
        lt: 'A_NO_DA',
        patterns: [[t => isRussianWord(t) && t.text !== 'ну' && t.text !== 'Ну', 'а', isWordOrNumber]],
        marked: [0, 1],
        caseSensitive: true,
        fix: comma,
    },
    {
        id: 'comma-before-no',
        lt: 'A_NO_DA',
        patterns: [[isRussianWord, 'но']],
        caseSensitive: true,
        fix: comma,
    },
    { id: 'kak-ni-stranno', lt: 'kak_ni_stranno', patterns: [['как', 'не', 'странно']], fix: phrase('как ни странно') },
    {
        id: 'kak-by-to-ni-bylo',
        lt: 'Kak_bi_to_ni_bilo',
        patterns: [
            ['как', 'бы', 'то', 'не', 'было'],
            ['как', 'бы-то', 'не|ни', 'было'],
        ],
        fix: phrase('как бы то ни было'),
    },
    {
        id: 'ni-ni',
        lt: 'Chastitsa_NI_v_ustojchiv_vyrazh',
        patterns: [
            ['не', 'свет', 'не', 'заря'],
            ['не', 'днём|днем', 'не', 'ночью'],
            ['не', 'много', 'не', 'мало'],
            ['не', 'пуха', 'не', 'пера'],
        ],
        fix: words => words.map(w => (w.toLowerCase() === 'не' ? caseLike(w, 'ни') : w)).join(' '),
    },
    { id: 'bolee-ili-menee', lt: 'Logical_bolee_menee', patterns: [['более', 'менее']], fix: phrase('более или менее') },
    {
        id: 'odin-za-drugim',
        lt: 'odin_za_odnim',
        patterns: [
            ['один', 'за', 'одним'],
            ['одна', 'за', 'одной'],
        ],
        marked: [2, 2],
        fix: ([w = '']) => caseLike(w, w.toLowerCase() === 'одним' ? 'другим' : 'другой'),
    },
    {
        id: 'prosit-proshcheniya',
        lt: 'prosit_proshenija',
        patterns: [
            [
                'просить|прошу|просишь|просит|просим|просите|просят|просил|просила|просили|проси|попросить|попрошу|попросит|попросим|попросят|попросил|попросила|попросили|попроси|попросите',
                'прощение',
            ],
        ],
        marked: [1, 1],
        fix: phrase('прощения'),
    },
    { id: 'skolko-vremeni', lt: 'skolko_vremeni', patterns: [['сколько', 'время']], marked: [1, 1], fix: phrase('времени') },
    {
        id: 's-dnem-rozhdeniya',
        lt: 'grammar_s_dnem_rojdenja',
        patterns: [['с', 'днём|днем', 'рожденья']],
        marked: [2, 2],
        fix: phrase('рождения'),
    },
    { id: 'neuzhto', lt: 'NEUJTO', patterns: [['не', 'уж', 'то']], fix: phrase('неужто') },
    {
        id: 'oplatit-bez-za',
        lt: 'Upotreblenije_predlogov',
        patterns: [
            [
                'оплатить|оплатил|оплатила|оплатили|оплачу|оплатишь|оплатит|оплатим|оплатите|оплатят|оплати|оплачивать|оплачиваю|оплачиваешь|оплачивает|оплачиваем|оплачиваете|оплачивают|оплачивал|оплачивала|оплачивали',
                'за',
            ],
        ],
        fix: firstWord,
    },
    {
        id: 'opisyvat-bez-pro',
        lt: 'Upotreblenije_predlogov',
        patterns: [
            [
                'описывать|описываю|описываешь|описывает|описываем|описываете|описывают|описывал|описывала|описывали|описать|опишу|опишешь|опишет|опишут|описал|описала|описали',
                'про',
            ],
        ],
        fix: firstWord,
    },
    {
        id: 's-utra-do-vechera',
        lt: 'Upotreblenije_predlogov',
        patterns: [['от', 'утра', 'до', 'вечера']],
        marked: [0, 1],
        fix: swap(0, 'с'),
    },
    { id: 'iz-shkoly', lt: 'Predlogi_iz_s', patterns: [['с', 'школы|командировки|армии']], fix: swap(0, 'из') },
    { id: 's-kavkaza', lt: 'Predlogi_iz_s', patterns: [['из', 'кавказа']], fix: swap(0, 'с') },
    {
        id: 'na-poverku',
        lt: 'na_poverku',
        patterns: [['на', 'проверку', 'оказалось|оказывается']],
        marked: [0, 1],
        fix: swap(1, 'поверку'),
    },
    {
        id: 'postavit-podpis',
        lt: 'Paronimy',
        patterns: [
            [
                'поставить|поставлю|поставит|поставил|поставила|поставили|поставь|поставьте|ставить|ставлю|ставит|ставил|ставила|ставь|ставьте',
                'роспись',
            ],
        ],
        marked: [1, 1],
        fix: phrase('подпись'),
    },
    {
        id: 'vdokhnut-vozdukh',
        lt: 'Paronimy',
        patterns: [
            ['вздохнуть|вздохну|вздохнул|вздохнула|вздохнули|вздохни|вздыхать|вздыхаю|вздыхает|вздыхал|вздыхала|вздыхали', 'воздух'],
        ],
        marked: [0, 0],
        fix: restem(/^вз/, 'в'),
    },
    {
        id: 'vnov-vozobnovit',
        lt: 'Tavtologija',
        patterns: [
            [
                'вновь',
                'возобновить|возобновлю|возобновит|возобновим|возобновят|возобновил|возобновила|возобновили|возобновлять|возобновляет|возобновляют',
            ],
        ],
        fix: words => caseLike(words[0] ?? '', words[1] ?? ''),
    },
    {
        id: 'listy-listya',
        lt: 'Sklonenije_suschestvit_PL',
        patterns: [
            ['листья', 'бумаги'],
            ['листы', 'деревьев'],
        ],
        marked: [0, 0],
        fix: ([w = '']) => caseLike(w, w.toLowerCase() === 'листья' ? 'листы' : 'листья'),
    },
    { id: 'nakanune', lt: 'Pravopisanie_slitno1', patterns: [['на', 'кануне']], fix: glue },
    { id: 'vdol-vzamen', lt: 'Pravopisanie_slitno1', patterns: [['в', 'доль|замен|глубь']], fix: glue },
    { id: 'zaranee', lt: 'Pravopisanie_slitno1', patterns: [['за', 'ранее']], unless: isWordOrNumber, fix: glue },
    {
        id: 'imet-v-vidu',
        lt: 'Pravopisanie_slitno1',
        patterns: [
            ['иметь|имею|имеешь|имеет|имеем|имеете|имеют|имел|имела|имели|имело|имей|имейте|имея|имелось|имеется|имеются|имелись', 'ввиду'],
        ],
        fix: ([verb = '', vvidu = '']) => `${verb} ${caseLike(vvidu, 'в виду')}`,
    },
    {
        id: 'to-zhe-samoe',
        lt: 'Pravopisanie_slitno1',
        // Only «самое»: «тоже самая умная» is correct Russian.
        patterns: [['тоже', 'самое']],
        fix: ([tozhe = '', samoe = '']) => `${caseLike(tozhe, 'то же')} ${samoe}`,
    },
] as const satisfies readonly GrammarRule[];

export type GrammarRuleId = (typeof RU_GRAMMAR_RULES)[number]['id'];

/** The stored «Больше не показывать» list, read back: the ids of rules that
 *  still exist, anything else dropped. */
export function normalizeGrammarRuleIds(raw: unknown): readonly GrammarRuleId[] {
    const known: ReadonlySet<string> = new Set(RU_GRAMMAR_RULES.map(rule => rule.id));
    return Array.isArray(raw) ? raw.filter((id): id is GrammarRuleId => typeof id === 'string' && known.has(id)) : [];
}
